// MAJLIS-64, interpreter mode on the listener's phone (D-43; approved mock-ups docs/spec/prototype/
// interpreter-mode-mockups.html, owner "GO" 2026-09-23):
//  - ListenSheet (UI-C21): the Listen choice - Translated (interpreter mode, the default, D-43(1)) or Original - and the
//    personal setting for the live voices while a phrase is spoken: lowered (default) or muted (D-43(2)). A choice is
//    sent as set_listen_mode at once; the setting is kept on this phone (prefs.ts). On iOS the setting is NOT offered:
//    iOS ignores element volume, so there the live voices are always muted under a voice (D-43, measured note);
//  - BehindMark (UI-C23): "N behind" with a Catch up tap, only while playback lags; it sits in the canvas, after the
//    newest phrase, as the mock-up places it (an open point for the owner: here, or beside the Listen control).
// The English interface prints the mock-up's Arabic companion lines (the sheet title, each option, "N behind"); the
// Arabic interface is Arabic throughout.
import { languageName } from "../data/languages";
import { useI18n } from "../i18n";
import { AR_BESIDE_EN, MOCKUP_ARABIC_LINES } from "../i18n/bilingual";
import type { LiveUnder } from "../prefs";
import type { ListenMode } from "../protocol/types";
import { Sheet } from "./Sheet";

/** Arabic-Indic digits for the Arabic "متأخر N" (the mock-up writes «متأخر ٢»). */
export function arabicDigits(n: number): string {
  return String(n).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[Number(d)]);
}

export function ListenSheet({
  mode,
  onMode,
  readLang,
  liveUnder,
  onLiveUnder,
  ios,
  onClose,
}: {
  mode: ListenMode;
  onMode: (mode: ListenMode) => void;
  readLang: string;
  liveUnder: LiveUnder;
  /** null on iOS: the setting is not offered there (muted is the only behaviour). */
  onLiveUnder: ((v: LiveUnder) => void) | null;
  /** iOS (detectIOS): the live voices are muted, never lowered, so the Translated option says so (ruling 2026-09-23). */
  ios: boolean;
  onClose: () => void;
}) {
  const { lang, t } = useI18n();
  const title = lang === "en" ? `${t("listenTitle")} · ${AR_BESIDE_EN.listenTitle}` : t("listenTitle");
  const options: Array<{ value: ListenMode; name: string; body: string; alt: string | null }> = [
    {
      value: "translated",
      name: t("listenTranslated"),
      body: t(ios ? "listenTranslatedBodyIOS" : "listenTranslatedBody", { lang: languageName(readLang, lang) }),
      alt: lang === "en" ? MOCKUP_ARABIC_LINES[ios ? "listenTranslatedIOS" : "listenTranslated"] : null,
    },
    {
      value: "original",
      name: t("listenOriginal"),
      body: t("listenOriginalBody"),
      alt: lang === "en" ? MOCKUP_ARABIC_LINES.listenOriginal : null,
    },
  ];
  const muted = liveUnder === "muted";
  return (
    <Sheet title={title} onClose={onClose} spec="UI-C21 D-43 UI-C10">
      <div className="listen-options" role="radiogroup" aria-label={t("listenTitle")}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={mode === o.value}
            className={`listen-option${mode === o.value ? " on" : ""}`}
            data-mode={o.value}
            onClick={() => {
              if (mode !== o.value) onMode(o.value);
            }}
            data-spec="UI-C21 D-43 P-17"
          >
            <span className="radio" aria-hidden="true" />
            <span className="listen-option-text">
              <b>{o.name}</b>
              <small>{o.body}</small>
              {o.alt && (
                <span className="listen-option-alt" lang="ar" dir="rtl">
                  {o.alt}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
      {onLiveUnder && (
        <div className="listen-under" data-spec="UI-C21 D-43">
          <span className="listen-under-text">
            {t("liveUnderRow")}
            <small>{t("liveUnderHint")}</small>
          </span>
          <button
            type="button"
            className="switch"
            role="switch"
            aria-checked={muted}
            aria-label={t("liveUnderSwitch")}
            onClick={() => onLiveUnder(muted ? "lowered" : "muted")}
          />
        </div>
      )}
    </Sheet>
  );
}

/** The count in bold inside its sentence, as the mock-up prints "<b>2</b> behind". */
function withCount(template: string, n: string) {
  const [before, after = ""] = template.split("{n}");
  return (
    <>
      {before}
      <b>{n}</b>
      {after}
    </>
  );
}

export function BehindMark({ n, onCatchUp }: { n: number; onCatchUp: () => void }) {
  const { lang, t } = useI18n();
  const arabic = withCount(AR_BESIDE_EN.behind, arabicDigits(n));
  return (
    <div className="behind" data-spec="UI-C23 D-43" data-behind={n}>
      <span className="behind-count">
        {lang === "ar" ? (
          arabic
        ) : (
          <>
            {withCount(t("behind", { n: "{n}" }), String(n))}
            {" · "}
            <span lang="ar" dir="rtl">
              {arabic}
            </span>
          </>
        )}
      </span>
      <button type="button" className="catch-up" onClick={onCatchUp} data-spec="UI-C23 D-43">
        <span className="catch-up-pill">{t("catchUp")}</span>
      </button>
    </div>
  );
}
